import { Injectable, NgZone, inject } from '@angular/core';

import { BrowserApiService } from '../common';
import { uniqId } from '../helpers';

import { isBrowserSupported, isWasmSupported } from './helpers';
import { RlottieOptions, RLOTTIE_OPTIONS } from './options';
import { InitMessage, WorkerMessage, WorkerMessageMethod } from './worker.interface';

@Injectable({
  providedIn: 'root'
})
export class MessageBusService {
  private readonly options = inject<RlottieOptions>(RLOTTIE_OPTIONS, { optional: true });
  private readonly browser = inject(BrowserApiService);
  private readonly ngZone = inject(NgZone);

  public isSupported = false;

  private readonly workers = new Map<number, Worker>();
  private readonly frameCache = new Map<string, Uint8ClampedArray>();
  private readonly listeners = new Map<string, Function>();
  private lastWorkerId = 0;

  constructor() {
    if(!this.options) {
      return;
    }

    this.setupWorkers();
  }

  assignWorkerId(): number {
    if(this.lastWorkerId > (this.options!.workersNum - 1)) {
      this.lastWorkerId = 0;
    }

    const workerToGive: number = this.lastWorkerId;

    this.lastWorkerId++;

    return workerToGive;
  }

  initRenderer(wId: number, data: InitMessage, callback: Function): void {
    const message = {
      data,
      method: WorkerMessageMethod.INIT
    };

    this.sendMessage(
      wId,
      message,
      callback
    );
  }

  getFrame(key: string, frameId: number): Uint8ClampedArray|undefined {
    return this.frameCache.get(`${ key }@${ frameId }`);
  }

  requestFrame(wId: number, key: string, frameId: number): void {
    const cacheKey = `${ key }@${ frameId }`;

    if(this.frameCache.get(cacheKey)) {
      return;
    }

    const message = {      
      method: WorkerMessageMethod.RENDER,
      data: { key, frameId }
    };

    this.sendMessage(
      wId,
      message,
      (arr: Uint8ClampedArray) => this.frameCache.set(cacheKey, arr),
      cacheKey
    );
  }

  cleanup(): void {
    this.workers.forEach((_, wId) => {
      this.sendMessage(wId, {
        method: WorkerMessageMethod.CLEANUP,
        data: null
      });
    });

    this.frameCache.clear();
  }

  private sendMessage(
    wId: number,
    message: WorkerMessage,
    callback: Function|null = null,
    customId: string|null = null
  ): void {
    const worker = this.workers.get(wId);

    if(!worker) {
      throw Error(`Worker with id "${ wId }" not found`);
    }

    const id = message.id = customId || uniqId(12);

    if(null !== callback) {
      this.listeners.set(id, (data: any) => callback(data.result));
    }

    worker.postMessage(message);
  }

  private setupWorkers(): void {
    if(this.browser.isServer() ||
      !isBrowserSupported() ||
      !isWasmSupported()
    ) {
      return;
    }

    this.isSupported = true;

    this.ngZone.runOutsideAngular(() => {
      for(let wId = 0; wId < this.options!.workersNum; wId++) {
        const worker = this.createWorker();
        
        this.workers.set(wId, worker);
      }
    });
  }

  private createWorker(): Worker {
    const worker = new Worker(new URL('./worker', import.meta.url), { type: 'classic' });

    worker.onmessage = ({ data }) => {
      const listener = !!data.id && this.listeners.get(data.id);

      if(listener) {
        listener(data);

        if(data.isComplete) {
          this.listeners.delete(data.id);
        }
      }
    };

    return worker;
  }
}